import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Chip,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import authService from '../services/authService';
import roleService from '../services/roleService';
import { PageTitleContainer } from '../components/common';

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0: Active, 1: Employee, 2: Client, 3: Archived
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [systemRoles, setSystemRoles] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Refs for height calculation
  const contentRef = useRef(null);
  const mainHeaderRef = useRef(null);
  const titleHeaderRef = useRef(null);

  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);

  // Fetch system roles for the dropdown
  useEffect(() => {
    const fetchSystemRoles = async () => {
      try {
        const response = await roleService.getAllRoles();
        setSystemRoles(response.roles || []);
      } catch (error) {
        console.error('Error fetching system roles:', error);
      }
    };
    fetchSystemRoles();
  }, []);

  // Check if the current user is an admin
  useEffect(() => {
    // First, let's check if we need to use validateToken instead
    const validateUser = async () => {
      try {
        // Get the current authenticated user from the API
        const currentUser = await authService.validateToken();
        console.log('Current validated user:', currentUser);

        // Check if user has admin role (or is siddu@infanion.com)
        if (!currentUser || (currentUser.systemRole !== 'admin' && currentUser.email !== 'siddu@infanion.com')) {
          console.log('Not authorized, redirecting to projects page');
          navigate('/projects');
        } else {
          console.log('User authorized as admin, fetching users...');
          fetchUsers();
        }
      } catch (error) {
        console.error('Error validating user:', error);
        navigate('/projects');
      }
    };

    validateUser();
  }, [navigate]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filter users based on tab and search query
  useEffect(() => {
    if (!users.length) {
      setFilteredUsers([]);
      return;
    }

    let filtered = [...users];

    // Apply tab filter
    switch (tabValue) {
      case 0: // Active - All active users
        filtered = filtered.filter(user => !user.isArchived);
        break;
      case 1: // Admin
        filtered = filtered.filter(user => !user.isArchived && user.systemRole === 'admin');
        break;
      case 2: // Regular User
        filtered = filtered.filter(user => !user.isArchived && user.systemRole === 'user');
        break;
      case 3: // Guest
        filtered = filtered.filter(user => !user.isArchived && user.systemRole === 'guest');
        break;
      case 4: // Customer
        filtered = filtered.filter(user => !user.isArchived && user.systemRole === 'customer');
        break;
      default:
        break;
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        (user.name && user.name.toLowerCase().includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query)) ||
        (user.firstName && user.firstName.toLowerCase().includes(query)) ||
        (user.lastName && user.lastName.toLowerCase().includes(query))
      );
    }

    setFilteredUsers(filtered);
  }, [users, tabValue, searchQuery]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();

      setUsers(data);
    } catch (error) {
      setNotification({
        open: true,
        message: error.toString(),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, user = null) => {
    setDialogMode(mode);
    if (mode === 'edit' && user) {
      setSelectedUserId(user._id);
      setFormData({
        name: user.name,
        email: user.email,
        password: '', // Don't show password, will only update if provided
        role: user.systemLevelRoleId || user.systemRole || 'user'
      });
    } else {
      setSelectedUserId(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (!formData.name || !formData.email || (dialogMode === 'create' && !formData.password)) {
        setNotification({
          open: true,
          message: 'Please fill all required fields',
          severity: 'error'
        });
        return;
      }

      // Prepare data for API with systemRole set from the form's role field
      const userData = {
        name: formData.name,
        email: formData.email,
        systemRole: formData.role, // Use the role from form as systemRole
        password: formData.password,
        role: formData.role // This will be processed by the backend to find the right Role document
      };

      if (dialogMode === 'create') {
        await userService.createUser(userData);
        setNotification({
          open: true,
          message: 'User created successfully',
          severity: 'success'
        });
      } else {
        // For edit, only include password if it's provided
        if (!userData.password) {
          delete userData.password;
        }
        await userService.updateUser(selectedUserId, userData);
        setNotification({
          open: true,
          message: 'User updated successfully',
          severity: 'success'
        });
      }

      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      setNotification({
        open: true,
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const handleDeleteConfirm = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteUser = async () => {
    try {
      await userService.deleteUser(userToDelete._id);
      setNotification({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
      fetchUsers();
    } catch (error) {
      setNotification({
        open: true,
        message: error.toString(),
        severity: 'error'
      });
    } finally {
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  return (
    <Box sx={{
      width: '100%',
      bgcolor: '#f5f6f7',
      minHeight: '100vh',
      pb: 4,
      px: 6,
      pt: 3,
      overflowX: 'hidden',
      backgroundImage: 'none', // Explicitly remove any background image
      '&::before': { display: 'none' },
      '&::after': { display: 'none' }
    }}>
      <PageTitleContainer
        ref={titleHeaderRef}
        title="Manage users"
        rightContent=<Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/create')}
          sx={{
            bgcolor: '#485869',
            color: 'white',
            py: 1.5,
            px: 3,
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#3A4754',
            },
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            textTransform: 'none !important'
          }}
        >
          Add new user
        </Button>
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', pt: 0, overflow: 'hidden', width: '100%' }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            ref={contentRef}
            sx={{
              width: '100%',
              height: contentHeight,
              overflow: 'hidden',
              boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
              borderRadius: '4px',
              bgcolor: 'white',
              overflowX: 'hidden',
              overflowY: 'auto',
              backgroundImage: 'none',
              '&::before': {
                display: 'none'
              },
              '&::after': {
                display: 'none'
              },
              m: '0px !important'
            }}
          >
            {/* Tabs for user types */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#475569',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#475569',
                    '&.Mui-selected': {
                      color: '#475569',
                      fontWeight: 600,
                    },
                  },
                  px: 3,
                  pt: 1
                }}
              >
                <Tab label="Active" id="tab-0" />
                <Tab label="Admin" id="tab-1" />
                <Tab label="Regular user" id="tab-2" />
                <Tab label="Guest" id="tab-3" />
                <Tab label="Customer" id="tab-4" />
              </Tabs>
            </Box>

            {/* Search Box and Content Container */}
            <Box sx={{ p: 5 }}>
              <Box sx={{
                mb: 4,
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <TextField
                  placeholder="Search users"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box component="span" sx={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '30%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      height: '45px',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '16px',
                      padding: '0 20px',
                      height: '45px',
                      boxSizing: 'border-box'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '15px',
                      opacity: 0.7
                    },
                    '& .MuiInputAdornment-root': {
                      display: 'none'
                    }
                  }}
                />
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                  <CircularProgress />
                </Box>
              ) : filteredUsers.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', my: 5 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>No users found</Typography>
                </Box>
                /* <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/users/create')}
                  sx={{
                    mt: 2,
                    bgcolor: '#485869',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#3a4a59',
                    },
                    borderRadius: '4px',
                    py: 1,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Add your first user
                </Button> */
              ) : (
                <TableContainer sx={{
                  maxHeight: 'calc(100vh - 425px)', overflow: 'auto', // Custom scrollbar styling
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-track': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    marginTop: '5px',
                    marginBottom: '5px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.4)',
                    },
                  },
                  '&::-webkit-scrollbar-corner': {
                    backgroundColor: 'transparent',
                  },
                }}>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead sx={{ position: 'sticky', top: ' 0', zIndex: '10' }}>
                      <TableRow>
                        <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '20%' }}>Name</TableCell>
                        <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '25%' }}>Email</TableCell>
                        <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '15%' }}>Role</TableCell>
                        <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '8%' }}>Status</TableCell>
                        <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '15%' }}>Created</TableCell>
                        <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow
                          key={user._id}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.03)'
                            },
                            '&:nth-of-type(even)': {
                              backgroundColor: 'white !important'
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {user.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.email}</Typography>
                          </TableCell>
                          <TableCell>
                          <Typography variant="body2">{user.systemLevelRoleName || user.systemRole || 'user'} </Typography>
                            {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: user.systemRole === 'admin' ? '#4DBCF3' : user.systemRole === 'guest' ? '#F89939' : user.systemRole === 'customer' ? '#9B59B6' : '#66CC33',
                                  mr: 1,
                                }}
                              />
                              <Chip
                                size="small"
                                label={user.systemRole || 'user'}
                                sx={{
                                  height: 22,
                                  fontSize: '0.75rem',
                                  bgcolor: user.systemRole === 'admin' ? '#4DBCF3' : user.systemRole === 'guest' ? '#F89939' : user.systemRole === 'customer' ? '#9B59B6' : '#66CC33',
                                  color: 'white'
                                }}
                              />
                            </Box> */}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label="Active"
                              color="success"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(user.createdAt || Date.now()).toLocaleDateString() || '--'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Box sx={{
                                mb: 1,
                                width: 32,
                                height: 32,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                                marginRight: '10px'
                              }}>
                                <Tooltip title="Edit" placement="left">

                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog('edit', user)}
                                    sx={{
                                      color: 'primary.main',
                                      '&:hover': {
                                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                                      }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box sx={{
                                mb: 1,
                                width: 32,
                                height: 32,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                              }}>
                                <Tooltip title="Delete" placement="left">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteConfirm(user)}
                                    disabled={user._id === (JSON.parse(localStorage.getItem('user'))?._id)}
                                    sx={{
                                      color: 'error.main',
                                      '&:hover': {
                                        bgcolor: 'rgba(211, 47, 47, 0.04)',
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        </Grid>
      </Box>

      {/* Create/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth

      >
        <DialogTitle sx={{ px: 4, pt: 5 }}>
          {dialogMode === 'create' ? (

            <>
              Create new user
            </>

          ) : (

            <>
              Edit user
            </>

          )}
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box component="form">
            {/* Full Name Field */}
            <Box sx={{ mb: 3, pt: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                Full name
              </Typography>
              <TextField
                className="single-line-field"
                name="name"
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter user's full name"
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    color: 'var(--color-blue, #475969)',
                    height: '45px',
                    transition: 'all 0.2s',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                    border: '1px solid #4759694d',
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: 'var(--color-blue, #475969)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px',
                  }
                }}
              />
            </Box>

            {/* Email Field */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                Email address
              </Typography>
              <TextField
                className="single-line-field"
                name="email"
                type="email"
                variant="outlined"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter user's email address"
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    color: 'var(--color-blue, #475969)',
                    height: '45px',
                    transition: 'all 0.2s',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                    border: '1px solid #4759694d',
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: 'var(--color-blue, #475969)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px',
                  }
                }}
              />
            </Box>

            {/* Password Field */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                {dialogMode === 'edit' ? 'New password (leave blank to keep current)' : 'Password'}
              </Typography>
              <TextField
                className="single-line-field"
                name="password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={formData.password}
                onChange={handleInputChange}
                required={dialogMode === 'create'}
                placeholder={dialogMode === 'edit' ? 'Enter new password (optional)' : 'Enter password'}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    color: 'var(--color-blue, #475969)',
                    height: '45px',
                    transition: 'all 0.2s',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                    border: '1px solid #4759694d',
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: 'var(--color-blue, #475969)',
                    paddingRight: '45px',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px',
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          mr: 0.5,
                          color: '#475969'
                        }}
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* User Role Field */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                User role
              </Typography>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: '45px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: 'var(--color-blue, #475969)',
                  }
                }}
              >
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  displayEmpty
                >
                  {systemRoles.length > 0 ? systemRoles.map(role => (
                    <MenuItem key={role.role_id} value={role.role_id || role.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#66CC33',
                            mr: 1.5,
                          }}
                        />
                        <Typography sx={{ fontSize: '14px' }}>{role.name}</Typography>
                      </Box>
                    </MenuItem>
                  )) : (
                  <>
                  <MenuItem value="user">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#66CC33',
                          mr: 1.5,
                        }}
                      />
                      <Typography sx={{ fontSize: '14px' }}>Regular User</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#4DBCF3',
                          mr: 1.5,
                        }}
                      />
                      <Typography sx={{ fontSize: '14px' }}>Administrator</Typography>
                    </Box>
                  </MenuItem>
                  </>
                  )}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#6c757d', fontSize: '12px', lineHeight: 1.5 }}>
                {formData.role === 'admin'
                  ? 'Administrators have full access to all features and can manage other users.'
                  : 'Regular users can only access their own projects and cannot manage other users.'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 2.5, borderTop: '1px solid rgba(0, 0, 0, 0.1)', bgcolor: 'white' }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#485869',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                bgcolor: 'rgba(72, 88, 105, 0.04)',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#3182CE',
              '&:hover': {
                backgroundColor: '#2C5282'
              },
              textTransform: 'none !important'
            }}
          >
            {dialogMode === 'create' ? 'Create user' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
       
      >
        <DialogTitle sx={{ px: 4, pt: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#fa5b5b' }}>
            Delete user account
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" paragraph>
            Are you sure you want to delete the user: <strong>{userToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" paragraph>
            This user's account will be permanently removed from the system. All associated projects and data will remain in the system, but will no longer be linked to this user.
          </Typography>
          <Box sx={{
            mt: 2,
            mb: 1,
            p: 2,
            bgcolor: 'rgba(250, 91, 91, 0.08)',
            borderRadius: '4px',
            borderLeft: '4px solid #fa5b5b'
          }}>
            <Typography color="error" variant="body2" fontWeight={500}>
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              color: '#485869',
              '&:hover': {
                bgcolor: 'rgba(72, 88, 105, 0.04)',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            sx={{
              bgcolor: '#fa5b5b',
              '&:hover': {
                bgcolor: '#fa2424',
              },
              boxShadow: 'none',
              textTransform: 'none !important'
            }}
          >
            Delete user
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;